import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { Box, Chip, Divider, Typography, useTheme } from '@mui/material'
import type { Totals } from 'types'
import type { VersionedDeal } from 'utils/agreementVersioning'
import { formatDateString } from 'utils/formatDayjsDateTime'

interface AmendmentSidebarProps {
  currentVersion: VersionedDeal
  currentTotals: Totals
  newTotals: Totals
  nextVersion: string
  changeType: 'major' | 'minor' | 'patch'
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value)

const DeltaIndicator = ({ current, next }: { current: number; next: number }) => {
  const diff = next - current
  if (Math.abs(diff) < 0.01) return null
  const isPositive = diff > 0
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {isPositive ? (
        <ArrowUpwardIcon sx={{ fontSize: 12, color: 'success.main' }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 12, color: 'error.main' }} />
      )}
      <Typography sx={{ fontSize: 10, color: isPositive ? 'success.main' : 'error.main', fontWeight: 600 }}>
        {formatCurrency(Math.abs(diff))}
      </Typography>
    </Box>
  )
}

const TotalsRow = ({ label, current, next }: { label: string; current: number; next: number }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
    <Typography sx={{ fontSize: 11, color: 'text.secondary', minWidth: 80 }}>{label}</Typography>
    <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.disabled' }}>
      {formatCurrency(current)}
    </Typography>
    <Typography sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 600 }}>{formatCurrency(next)}</Typography>
    <DeltaIndicator current={current} next={next} />
  </Box>
)

const AmendmentSidebar = ({ currentVersion, currentTotals, newTotals, nextVersion, changeType }: AmendmentSidebarProps) => {
  const theme = useTheme()
  const deal = currentVersion.deal

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(107, 185, 240, 0.04)' : 'rgba(107, 185, 240, 0.02)',
        border: '1px solid',
        borderColor: theme.palette.divider,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Current Deal Info */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            Current Version
          </Typography>
          <Chip
            label={`v${currentVersion.semanticVersion}`}
            size="small"
            color="default"
            sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, fontSize: 10 }}
          />
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>{deal.name || 'Unnamed Deal'}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {deal.startDate ? formatDateString(deal.startDate) : '—'} to{' '}
          {deal.endDate ? formatDateString(deal.endDate) : '—'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {currentVersion.products.length} product{currentVersion.products.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Divider />

      {/* New Version Info */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>
            New Version
          </Typography>
          <Chip
            label={`v${nextVersion}`}
            size="small"
            color="primary"
            sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, fontSize: 10 }}
          />
        </Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {changeType === 'minor' ? 'Add/Remove Products' : 'Budget/Pricing Change'}
        </Typography>
      </Box>

      <Divider />

      {/* Totals Comparison */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>Metric</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Current</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>New</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Delta</Typography>
        </Box>
        <TotalsRow label="Monthly" current={currentTotals.monthlyTotal} next={newTotals.monthlyTotal} />
        <TotalsRow label="Gross Profit" current={currentTotals.grossProfit} next={newTotals.grossProfit} />
        <TotalsRow label="Total" current={currentTotals.total} next={newTotals.total} />
        <TotalsRow
          label="Total GP"
          current={currentTotals.totalGrossProfit}
          next={newTotals.totalGrossProfit}
        />
      </Box>
    </Box>
  )
}

export default AmendmentSidebar
